/**
 * A `Select` bound to a `react-hook-form` field.
 *
 * Radix's Select is not a form-native control — there is no `<select name>` in
 * the output — so `{...register('level')}` silently does nothing, which is the
 * worst failure shape available: the form looks wired and saves the default
 * forever. `Controller` is the supported binding, and wrapping it here keeps six
 * call sites from each carrying the same eight lines of boilerplate.
 *
 * The label, hint and error still come from `Field`, so this only replaces the
 * control:
 *
 *     <Field label={t('level')} error={errors.level?.message}>
 *       <SelectField control={form.control} name="level" options={LEVELS} />
 *     </Field>
 */
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form'

import { Select, type SelectOption } from './Select'

export type SelectFieldProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  options: readonly SelectOption[]
  placeholder?: string | undefined
  clearable?: boolean | undefined
  disabled?: boolean | undefined
  className?: string | undefined
}

export function SelectField<T extends FieldValues>({
  control,
  name,
  options,
  placeholder,
  clearable,
  disabled,
  className,
}: SelectFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Select
          options={options}
          // `?? ''` — an undefined default would make Radix treat the field as
          // uncontrolled and stop reflecting `form.reset()`, which is how the
          // settings screen loads a course.
          value={field.value ?? ''}
          onValueChange={field.onChange}
          name={field.name}
          {...(placeholder ? { placeholder } : {})}
          {...(clearable ? { clearable } : {})}
          {...((disabled ?? field.disabled) ? { disabled: true } : {})}
          {...(className ? { className } : {})}
        />
      )}
    />
  )
}
